import React from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { Spinner } from '../../ui';
import { SpecDecision, SpecMismatchView, specMismatchLabel } from '../../discovery/discoveryModel';

/**
 * Spec safety rail above the conversation: the supplier offers something other
 * than the BOM line states. Until the buyer accepts theirs, Robbie's emails ask
 * for the buyer's spec (the backend enforces it on every send); this banner is
 * where the buyer sees that before sending and can change it.
 */
interface SpecMismatchBannerProps {
  mismatches: SpecMismatchView[];
  decision: SpecDecision;
  /** Absent for a viewer: the finding shows, the choice does not. */
  onDecide?: (decision: 'ASK_FOR_OURS' | 'ACCEPT_THEIRS') => Promise<boolean>;
  isSaving?: boolean;
  error: string | null;
}

const ChoiceButton: React.FC<{ chosen: boolean; disabled: boolean; onClick: () => void; children: React.ReactNode }> = ({
  chosen,
  disabled,
  onClick,
  children,
}) => (
  <button
    type="button"
    aria-pressed={chosen}
    disabled={disabled}
    onClick={onClick}
    className={`flex shrink-0 items-center gap-s2 rounded-control border px-s4 py-[6px] font-sans text-body-sm font-semibold disabled:opacity-60 ${
      chosen ? 'border-warn-ink bg-warn-ink text-white' : 'border-warn-border bg-surface text-warn-ink hover:border-warn-ink'
    }`}
  >
    {chosen && <Check className="h-3 w-3" />}
    {children}
  </button>
);

const SpecMismatchBanner: React.FC<SpecMismatchBannerProps> = ({ mismatches, decision, onDecide, isSaving = false, error }) => {
  const accepted = decision === 'ACCEPT_THEIRS';
  const ours = mismatches.map((m) => m.requested).join(', ');
  const theirs = mismatches.map((m) => m.offered).join(', ');

  return (
    <div className="shrink-0 border-b border-warn-border bg-warn-bg px-[16px] py-[12px]" role="status">
      <div className="flex items-start gap-s3">
        <AlertTriangle className="mt-[2px] h-4 w-4 shrink-0 text-warn-ink" />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-body-sm font-bold text-warn-ink">
            This supplier offers a different spec — ask for yours, or accept theirs
          </p>
          <p className="m-0 mt-[2px] text-meta text-ink-body">{specMismatchLabel(mismatches)}</p>
          <p className="m-0 mt-[2px] text-meta text-ink-secondary">
            {accepted
              ? `You accepted their spec — I’ll ask about ${theirs}.`
              : `Every message I send them asks for your ${ours} until you accept theirs.`}
          </p>
          {onDecide && (
            <div className="mt-s3 flex flex-wrap items-center gap-s2">
              <ChoiceButton chosen={!accepted} disabled={isSaving} onClick={() => accepted && onDecide('ASK_FOR_OURS')}>
                Ask for mine ({ours})
              </ChoiceButton>
              <ChoiceButton chosen={accepted} disabled={isSaving} onClick={() => !accepted && onDecide('ACCEPT_THEIRS')}>
                Accept theirs ({theirs})
              </ChoiceButton>
              {isSaving && <Spinner size="sm" />}
            </div>
          )}
          {error && (
            <p className="m-0 mt-s2 flex items-center gap-s1 text-meta font-semibold text-danger-ink">
              <AlertTriangle className="h-3 w-3 shrink-0" /> {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpecMismatchBanner;
